import {
  type Dispatch,
  type ReducerAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { ASSIGNMENT_SET_MULTIPLE } from 'components/Charts/ScheduledActivities/assignmentHelpers';
import { useToast, toastVariant } from '__components/ToastProvider';
import { useCamelCaseActivityQuery } from '__queries/models/activites';
import ActivityService from 'services/ActivityService';
import { getOriginalError } from 'services/AxiosService';
import { useActivityRules } from 'ts-activities/useActivityRules';
import {
  FORMDATA_FIELDS,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  FORM_ACTIONS,
} from './config';
import {
  useActivityAssociation,
  getFieldValueFromAPI,
  isEmptyValue,
  mapFields,
  setInitialValue,
} from './helpers';

type Field = {
  id: string;
  name: string;
  displayName: string;
  customObjectField: null | {
    id: string;
    name: string;
    displayName: string;
    fieldType: string;
    isDefault: boolean;
    customObject: {
      id: string;
      name: string;
      objectType: string;
      objectName: string;
      entityName: string;
      fetchUrl: string;
      associationSource: string;
    };
  };
  isRequired: boolean;
  isReadOnly: boolean;
  isHidden: boolean;
  isDeletable: boolean;
  isHideable: boolean;
  order: number;
  meta: { cols?: number; isReadonly?: boolean };
  properties: {
    allowOptions: boolean;
    allowMultiple: boolean;
    allowRelations: boolean;
    allowAdd: boolean;
    type: string;
    targetColumn: string;
  };
  relation: any;
  rating: any;
  phonenumberOptions: any;
  decimalOptions: any;
  fieldType: string;
  options: {
    id: string;
    code: string;
    name: string;
    order: number;
    meta: Record<string, any>;
  }[];
  access: { edit: boolean };
};

export type FormData = {
  fields: {
    customObject?: string;
    customObjectFieldId?: string;
    displayName: string;
    fieldData: Field;
    fieldType: string;
    id: string;
    name: string;
    value: any;
    valuePlaceholder: any;
  }[];
  mentions: any[];
  notes: string;
  relatedObjects: {
    customObject: string;
    entityId: string;
    isContact: boolean;
  }[];
  scheduledActivityId?: string;
};

type EntityFieldValue = {
  display_name: string;
  fieldType: string;
  id: string;
  value: any;
};

export type CompleteActivityWizardContext = {
  activity: any;
  activityFields: Field[];
  assignments: Record<string, any>;
  formData: FormData;
  isComplete: boolean;
  isDirty: boolean;
  isLoadingActivityAssociations: boolean;
  isLoading: boolean;
  loadActivityError: boolean;
  /**
   * @param id - a field's id or text block's internal_name
   */
  isRuleActiveForLayoutItem(id: string): boolean;
  resetField(): void;
  shouldDisableField(field: Field): boolean;
  updateField(
    field: any,
    value: any | ((v: any[]) => any[]),
    shouldIgnoreDirty?: boolean
  ): void;
  updateValidation(step: any, isValid?: null): void;

  activityRelatedObjects: any;
  dispatchAssignment: Dispatch<ReducerAction<any>>;
};

type CompleteActivityWizardContextProps = {
  children: any;
  data: {
    id?: string;
    activityObject: Record<string, any>;
    associatedEntities: any[];
    mentions?: any[];
    note?: string;
  };
  isLoadingPredefinedOptions: boolean;
  predefinedOptions?: {
    clientOption: { value: string; label: string };
    companyOption: {
      id?: string;
      display_name?: string;
      name?: string;
    };
    defaultAssignment: Record<string, { value: string; label: string }> & {
      objectId: string;
    };
  };
};

type UseActivityConfigurationResult = Pick<
  CompleteActivityWizardContext,
  | 'isComplete'
  | 'isDirty'
  | 'formData'
  | 'resetField'
  | 'updateField'
  | 'updateValidation'
>;

const context = createContext<CompleteActivityWizardContext>({
  activity: null,
  activityFields: EMPTY_ARRAY,
  assignments: {},
  activityRelatedObjects: [],
  formData: {
    fields: EMPTY_ARRAY,
    mentions: EMPTY_ARRAY,
    notes: '',
    relatedObjects: EMPTY_ARRAY,
  },
  isComplete: false,
  isDirty: false,
  isLoading: false,
  isLoadingActivityAssociations: false,
  loadActivityError: false,
  dispatchAssignment: () => null,
  isRuleActiveForLayoutItem: () => true,
  resetField: () => null,
  shouldDisableField: () => false,
  updateField: () => null,
  updateValidation: () => null,
});

export const useCompleteActivityWizardContext = () => {
  return useContext(context);
};

const STEP_ACTIONS = {
  validate: 'VALIDATE',
};

const { SCHEDULED_ACTIVITY_ID, NOTES, FIELDS, RELATED_OBJECTS, MENTIONS } =
  FORMDATA_FIELDS;

const activityInit = (scheduledActivity: any) => ({
  [SCHEDULED_ACTIVITY_ID]: scheduledActivity.id,
  [NOTES]: scheduledActivity.note || '',
  [FIELDS]: EMPTY_ARRAY,
  [RELATED_OBJECTS]: EMPTY_ARRAY,
  [MENTIONS]: scheduledActivity.mentions
    ? scheduledActivity.mentions.map(({ id }: any) => id)
    : EMPTY_ARRAY,
});

const stepValReducer = (state: any, { type, payload }: any) => {
  switch (type) {
    case STEP_ACTIONS.validate: {
      const { step, isValid } = payload;
      if (step in state && state[step] === isValid) {
        return state;
      }
      if (!(step in state) && isValid === null) {
        return state;
      }
      const nextStepValidation = {
        ...state,
        [step]: isValid,
      };
      Object.keys(nextStepValidation).forEach(
        (k) => nextStepValidation[k] === null && delete nextStepValidation[k]
      );
      return nextStepValidation;
    }
    default:
      throw new Error('Unknown step validation action');
  }
};

const activityReducer = (state: any, { type, payload }: any) => {
  switch (type) {
    case FORM_ACTIONS.update: {
      const { field, value } = payload;
      return {
        ...state,
        [field]: typeof value === 'function' ? value(state[field]) : value,
      };
    }
    case FORM_ACTIONS.reset: {
      const { scheduledActivity } = payload;
      return activityInit(scheduledActivity);
    }
    default:
      throw new Error('Unknown action');
  }
};

function useActivityConfiguration(
  scheduledActivity: any = {}
): UseActivityConfigurationResult {
  const [formData, dispatchFormData] = useReducer(
    activityReducer,
    scheduledActivity,
    activityInit
  );
  const [isDirty, setDirty] = useState(false);

  const updateField = useCallback(
    (f: any, value: any, shouldIgnoreDirty: boolean) => {
      !shouldIgnoreDirty && setDirty(true);
      dispatchFormData({
        type: FORM_ACTIONS.update,
        payload: { field: f, value },
      });
    },
    [dispatchFormData]
  );

  const resetField = useCallback(() => {
    setDirty(false);
    dispatchFormData({
      type: FORM_ACTIONS.reset,
      payload: { scheduledActivity },
    });
  }, [scheduledActivity, dispatchFormData]);

  const [stepValidation, dispatchStepValidation] = useReducer(
    stepValReducer,
    EMPTY_OBJECT
  );

  const updateValidation = useCallback(
    (step: any, isValid = null) => {
      dispatchStepValidation({
        type: STEP_ACTIONS.validate,
        payload: { step, isValid },
      });
    },
    [dispatchStepValidation]
  );

  const isComplete = Object.keys(stepValidation).every(
    (step) => !!stepValidation[step] || stepValidation[step] === null
  );

  return {
    formData,
    updateField,
    resetField,
    updateValidation,
    isComplete,
    isDirty,
  };
}

export const CompleteActivityWizardContext = (
  props: CompleteActivityWizardContextProps
) => {
  const { children, data, isLoadingPredefinedOptions, predefinedOptions } =
    props;
  const activityId = data?.activityObject?.id;

  const { t } = useTranslation();
  const [showToast] = useToast();

  const {
    data: activity,
    error,
    isError,
    isLoading: loadingActivity,
  } = useCamelCaseActivityQuery(activityId, {
    retry: false,
  });

  const {
    formData,
    updateField,
    resetField,
    updateValidation,
    isComplete,
    isDirty,
  } = useActivityConfiguration(data);

  const [loadingEntityFieldValues, setLoadingEntityFieldValues] =
    useState(false);

  const entityValuesRef = useRef<
    Record<string, Record<string, EntityFieldValue['value']>>
  >({});
  const fetchedEntitiesRef = useRef<Record<string, boolean>>({});

  const { fields, relatedObjects } = formData;

  const shouldDisableField = useCallback(
    (field: Field) => {
      const readonly = Boolean(field.meta.isReadonly);

      const hasInitialValue = relatedObjects.some((obj) => {
        return entityValuesRef.current[obj.entityId]?.[field.id];
      });

      if (!hasInitialValue && field.isRequired && readonly) {
        return false;
      }

      return readonly;
    },
    [relatedObjects]
  );

  const { isRuleActiveForLayoutItem, loading: activityRulesLoading } =
    useActivityRules(activity?.visibilityRules, fields);

  const {
    assignments,
    dispatchAssignment,
    activityRelatedObjects,
    isFetching: isLoadingActivityAssociations,
  } = useActivityAssociation(activityId, predefinedOptions, {
    retry: false,
  });

  const { value: activityFields = [], loading: loadingActivityFields } =
    useAsync(async (): Promise<Field[]> => {
      const rawFields = activityId
        ? await ActivityService.getObjectFields({
            activityObjectId: activityId,
          })
        : [];
      return rawFields.map(mapFields);
    }, [activityId]);

  useEffect(() => {
    if (isError) {
      const err = ActivityService.notFoundError(error);

      const orig = getOriginalError(err);
      showToast({
        message:
          orig?.message ||
          t(
            'There was an error loading the {{activityName}}. Please fix and try again.',
            {
              activityName: t('Chosen Activity'),
            }
          ),
        variant: toastVariant.FAILURE,
      });
    }
  }, [isError, error, showToast, t]);

  useEffect(() => {
    if (data?.associatedEntities?.length && activityRelatedObjects.length) {
      const value = data.associatedEntities.reduce((acc, entity) => {
        const relObject = activityRelatedObjects.find(
          (act) => act.id === entity.customObjectId
        );
        if (relObject) {
          const { name } = relObject.component.setterProp;
          const value = {
            label: entity.displayName || entity.name,
            value: entity.entityId,
          };
          acc[name] = value;
        }
        return acc;
      }, {});
      dispatchAssignment({
        type: ASSIGNMENT_SET_MULTIPLE,
        value,
      });
    }
  }, [data, activityRelatedObjects, dispatchAssignment]);

  const setInitialValues = useCallback(() => {
    updateField(
      FIELDS,
      (prevFields: FormData['fields']) => {
        return activityFields.map((activityField) => {
          const { displayName, fieldType, id, customObjectField, name } =
            activityField;
          const prevValue = prevFields.find((field) => field?.id === id)?.value;
          return {
            id,
            displayName,
            name,
            fieldType,
            customObjectFieldId: customObjectField?.id,
            customObject: customObjectField?.customObject.id,
            value: isEmptyValue(prevValue)
              ? setInitialValue({ fieldType })
              : prevValue,
            fieldData: activityField,
          };
        });
      },
      true
    );
  }, [activityFields, updateField]);

  useEffect(() => {
    setInitialValues();
  }, [setInitialValues]);

  const setValues = useCallback(
    async (objectId: string, recordId: string) => {
      let fieldsValues: EntityFieldValue[] = [];

      if (objectId && recordId) {
        fieldsValues = await ActivityService.getEntityRecordsFieldValues(
          activityId,
          recordId
        );
        entityValuesRef.current[recordId] = {};
      }

      updateField(
        FIELDS,
        (prev: FormData['fields']) => {
          return prev.map((prevField) => {
            const shouldFieldBeUpdated = prevField.customObject === objectId;
            const fieldFromAPI =
              shouldFieldBeUpdated &&
              fieldsValues.find((f) => f.id === prevField.customObjectFieldId);

            if (fieldFromAPI) {
              entityValuesRef.current[recordId][prevField.id] =
                fieldFromAPI.value;
            }

            if (shouldFieldBeUpdated && !fieldFromAPI) {
              return {
                ...prevField,
                value: setInitialValue(
                  activityFields.find(({ id }) => id === prevField.id)
                ),
              };
            }
            return fieldFromAPI
              ? {
                  ...fieldFromAPI,
                  id: prevField.id,
                  customObject: objectId,
                  customObjectFieldId: prevField.customObjectFieldId,
                  value: isEmptyValue(fieldFromAPI.value)
                    ? setInitialValue(
                        activityFields.find(({ id }) => id === prevField.id)
                      )
                    : getFieldValueFromAPI(fieldFromAPI),
                  fieldData: prevField.fieldData,
                }
              : prevField;
          });
        },
        true
      );
    },
    [activityId, updateField, activityFields]
  );

  // this will run whenever we change any association, it will map through all associations to decide to fetch selected entitites' field values
  // and here are the rules:
  // 1. if we did not have this entitiy before the change, we fetch it's values and replace previuosly set (or empty) fields with the fetched
  // values.
  // 2. if we had this entity before the change, we do not fetch it's values again and leave fields in previous state.
  // 3. if the association was cleared (entity removed) we leave fields in previous state.
  const loadRelatedObjectFieldValues =
    !!activityFields.length && !!relatedObjects.length && !!activityId;

  useEffect(() => {
    const load = async () => {
      setLoadingEntityFieldValues(true);

      const lookup = relatedObjects.reduce<Record<string, boolean>>(
        (acc, { entityId }) => {
          acc[entityId] = true;
          return acc;
        },
        {}
      );

      // filter out fetched entities that are not in the current related objects
      fetchedEntitiesRef.current = Object.keys(
        fetchedEntitiesRef.current
      ).reduce<Record<string, boolean>>((acc, key) => {
        if (lookup[key]) {
          acc[key] = fetchedEntitiesRef.current[key];
        }
        return acc;
      }, {});

      const promises: Promise<void>[] = [];

      // map through related objects and fetch field values for newly added
      relatedObjects.forEach(({ customObject, entityId }) => {
        if (!fetchedEntitiesRef.current[entityId]) {
          promises.push(setValues(customObject, entityId));
          fetchedEntitiesRef.current[entityId] = !!entityId;
        }
      });

      await Promise.all(promises);
      setLoadingEntityFieldValues(false);
    };

    if (loadRelatedObjectFieldValues) {
      load();
    } else {
      setInitialValues();
    }
  }, [
    loadRelatedObjectFieldValues,
    relatedObjects,
    setValues,
    setInitialValues,
  ]);

  const isLoading =
    loadingActivity ||
    loadingActivityFields ||
    activityRulesLoading ||
    isLoadingActivityAssociations ||
    loadingEntityFieldValues ||
    isLoadingPredefinedOptions;

  const ctx = {
    activity,
    activityFields,
    activityRelatedObjects,
    assignments,
    formData,
    isComplete,
    isDirty,
    isLoadingActivityAssociations,
    isLoading,
    loadActivityError: isError,
    dispatchAssignment,
    isRuleActiveForLayoutItem,
    resetField,
    shouldDisableField,
    updateField,
    updateValidation,
  };

  return <context.Provider value={ctx}>{children}</context.Provider>;
};
